import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { Fade } from 'react-awesome-reveal'
import { useSelector } from 'react-redux'

import { ComponentErrorBoundary } from '@/components/atomic/ComponentErrorBoundary'
import { Loader } from '@/components/atomic/Loader'
import { SellYourVehicleBanner } from '@/components/styleguide/SellYourVehicleBanner'
import { NoResults } from '@/components/views/SearchResults/views/NoResults'
import { Pagination } from '@/components/views/SearchResults/views/Pagination'
import { SearchResultList } from '@/components/views/SearchResults/views/SearchResultList'
import { SearchResultsContainer } from '@/components/views/SearchResults/views/SearchResultsContainer'
import {
  constants,
  FILTER_LEASE_TYPES_BUSINESS,
  FILTER_LEASE_TYPES_PERSONAL,
} from '@/lib/constants'
import { ERRORS } from '@/lib/errors'
import { selectErrorMessage } from '@/store/selectors/error'
import { selectLeaseType, selectSearchResultDeals } from '@/store/selectors/search'

const LocoSnaps = dynamic(() => import('@/components/views/LocoSnaps'), { ssr: false })

export const SearchResults = ({ isVans, status, layoutType = 'grid' }) => {
  const [resultCount, setResultCount] = useState(0)
  const [componentStatus, setComponentStatus] = useState(constants.componentStatus.LOADING)
  const leaseType = useSelector(selectLeaseType)
  const results = useSelector(selectSearchResultDeals)
  const error = useSelector(state => selectErrorMessage(state, ERRORS.fetchingSearchResults))

  useEffect(() => {
    setResultCount(results.length)
    if (results.length === 0) {
      setComponentStatus(constants.componentStatus.NOT_FOUND)
    }
  }, [results])

  useEffect(() => {
    if (status === constants.componentStatus.LOADING) {
      setComponentStatus(constants.componentStatus.LOADING)
    } else {
      setComponentStatus(constants.componentStatus.COMPLETE)
    }
  }, [status])

  if (error) {
    return (
      <SearchResultsContainer
        isLoading={componentStatus === constants.componentStatus.LOADING}
        hasNoResults={true}
        isVans={isVans}>
        <div className="row">
          <div className="col-12 p-0 mt-3">
            <div
              className="alert alert-warning"
              role="alert">
              {error}
            </div>
          </div>
        </div>
        <SellYourVehicleBanner
          position="bottom"
          page="browse"
          isVans={isVans}
        />
      </SearchResultsContainer>
    )
  }

  if (
    status !== constants.componentStatus.COMPLETE &&
    componentStatus !== constants.componentStatus.COMPLETE
  ) {
    return (
      <>
        <SearchResultsContainer isVans={isVans}>
          <div className="container--search-loader">
            <Fade triggerOnce>
              <div className="loader__wrapper">
                <Loader message="Searching" />
              </div>
            </Fade>
          </div>
        </SearchResultsContainer>
        <SellYourVehicleBanner
          position="bottom"
          page="browse"
          isVans={isVans}
        />
      </>
    )
  }

  return (
    <>
      <SearchResultsContainer
        isVans={isVans}
        layout={layoutType}
        hasNoResults={resultCount === 0 && componentStatus === constants.componentStatus.COMPLETE}>
        <ComponentErrorBoundary>
          {resultCount > 0 && componentStatus === constants.componentStatus.COMPLETE ? (
            <SearchResultList
              layoutType={layoutType}
              items={
                isVans || leaseType === FILTER_LEASE_TYPES_BUSINESS ? results : results.slice(0, 6)
              }
            />
          ) : (
            <NoResults isVans={isVans} />
          )}
          {!isVans &&
            leaseType === FILTER_LEASE_TYPES_PERSONAL &&
            resultCount > 0 &&
            componentStatus === constants.componentStatus.COMPLETE && (
              <>
                <LocoSnaps leaseType={leaseType} />
                {resultCount > 6 && <SearchResultList items={results.slice(6)} />}
              </>
            )}
          {resultCount > 0 && (
            <>
              <Pagination />
              <SellYourVehicleBanner
                position="bottom"
                page="browse"
                isVans={isVans}
              />
            </>
          )}
        </ComponentErrorBoundary>
      </SearchResultsContainer>
    </>
  )
}
